import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

function fmt(v) { if (!v) return '—'; try { return new Date(v).toLocaleString(); } catch { return v; } }

function NewKeyModal({ scopes, onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', scopes: ['risk:trigger', 'risk:read'], allowed_repos: '', rate_limit_per_minute: 60, expires_at: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function submit(e) {
    e.preventDefault(); setBusy(true); setError('');
    try {
      const payload = {
        name: form.name, scopes: form.scopes, rate_limit_per_minute: Number(form.rate_limit_per_minute) || 60,
        allowed_repos: form.allowed_repos.split(/[\n,]/).map(s => s.trim()).filter(Boolean),
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : undefined,
      };
      const res = await axios.post('/api-keys', payload);
      onCreated(res.data);
    } catch (err) { setError(err.response?.data?.error || 'Failed to create the key'); }
    finally { setBusy(false); }
  }
  function toggle(s) { setForm(f => ({ ...f, scopes: f.scopes.includes(s) ? f.scopes.filter(x => x !== s) : [...f.scopes, s] })); }
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header"><h3>New API key</h3></div>
        <form onSubmit={submit}>
          <div className="modal-body">
            <div className="form-group"><label className="form-label">Name (who uses it)</label>
              <input className="form-input" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="GitLab CI – AMEYO_STACK group" /></div>
            <div className="form-group"><label className="form-label">Scopes</label>
              {Object.entries(scopes).map(([s, d]) => (
                <label key={s} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginTop: 4 }}>
                  <input type="checkbox" checked={form.scopes.includes(s)} onChange={() => toggle(s)} /><code>{s}</code><span style={{ color: 'var(--text-muted)' }}>{d}</span>
                </label>))}
            </div>
            <div className="form-group"><label className="form-label">Allowed repositories (globs, one per line; blank = any)</label>
              <textarea className="form-input" rows={3} value={form.allowed_repos} onChange={e => setForm({ ...form, allowed_repos: e.target.value })} placeholder={'AMEYO_STACK/*\nameyo_cloud_stack/central-fetcher'} /></div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Rate limit (requests / minute)</label>
                <input className="form-input" type="number" min={1} max={6000} value={form.rate_limit_per_minute} onChange={e => setForm({ ...form, rate_limit_per_minute: e.target.value })} /></div>
              <div className="form-group"><label className="form-label">Expires (optional)</label>
                <input className="form-input" type="datetime-local" value={form.expires_at} onChange={e => setForm({ ...form, expires_at: e.target.value })} /></div>
            </div>
            {error && <div className="error-msg">{error}</div>}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={busy || !form.scopes.length}>{busy ? 'Creating…' : 'Create key'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RiskSettings() {
  const [s, setS] = useState(null);
  const [status, setStatus] = useState(null);
  // Same catalogue the code-review config form uses: curated ids, live-checked
  // against Anthropic's model list, plus the app default a blank value resolves to.
  const [models, setModels] = useState({ models: [], default: '' });
  useEffect(() => {
    axios.get('/risk-analysis/settings').then(r => setS(r.data)).catch(() => setStatus({ type: 'error', message: 'Failed to load worker settings' }));
    axios.get('/code-review/models').then(r => setModels({ models: Array.isArray(r.data.models) ? r.data.models : [], default: r.data.default || '' })).catch(() => {});
  }, []);
  if (!s) return null;
  async function save(e) {
    e.preventDefault(); setStatus(null);
    try { const r = await axios.put('/risk-analysis/settings', s); setS(r.data.settings); setStatus({ type: 'success', message: 'Worker settings saved. The worker picks them up on its next poll.' }); }
    catch (err) { setStatus({ type: 'error', message: err.response?.data?.error || 'Failed to save' }); }
  }
  const f = (k, label, type = 'text', hint) => (
    <div className="form-group"><label className="form-label">{label}</label>
      <input className="form-input" type={type} value={s[k] ?? ''} onChange={e => setS({ ...s, [k]: e.target.value })} />
      {hint && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{hint}</div>}</div>);
  return (
    <form onSubmit={save} className="card" style={{ marginTop: 20 }}>
      <h3 style={{ marginTop: 0 }}>Regression-risk worker</h3>
      <div className="form-row">
        {f('risk_worker_concurrency', 'Concurrent analyses', 'number', 'One per repository at a time regardless; three is the tested ceiling on the ECC box.')}
        {f('risk_daily_cost_cap_usd', 'Daily cost cap (USD)', 'number', 'Blank = unlimited. Triggers return 429 once reached.')}
        {f('risk_run_timeout_minutes', 'Run timeout (minutes)', 'number')}
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Model</label>
          <select className="form-input" value={s.risk_model || ''} onChange={e => setS({ ...s, risk_model: e.target.value })}>
            <option value="">App default{models.default ? ` (${models.default})` : ''}</option>
            {models.models.map(m => (
              <option key={m.id} value={m.id} disabled={m.available === false}>
                {m.label}{m.recommended ? ' — recommended' : ''}{m.available === false ? ' — not served by the API' : ''}
              </option>
            ))}
            {s.risk_model && !models.models.some(m => m.id === s.risk_model) && <option value={s.risk_model}>{s.risk_model} (not in catalogue)</option>}
          </select>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>The analyst runs on Claude Code; Opus is the tested tier. Each analysis costs roughly $4–5 on Opus.</div>
        </div>
        {f('risk_agents_repo_url', 'ecctestagents repository')}
      </div>
      <div className="form-row">
        {f('risk_agents_branch', 'Agent branch')}
        {f('risk_ledger_push_branch', 'Ledger push branch', 'text', 'The worker commits risk ledgers here after each run; merge it into main by PR.')}
      </div>
      {status && <div className={status.type === 'error' ? 'error-msg' : ''} style={{ fontSize: 13, color: status.type === 'success' ? 'var(--success)' : undefined, marginBottom: 8 }}>{status.message}</div>}
      <button className="btn btn-primary" type="submit">Save worker settings</button>
    </form>
  );
}

export default function ApiKeysPage() {
  const [keys, setKeys] = useState([]);
  const [scopes, setScopes] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [created, setCreated] = useState(null);
  const [audit, setAudit] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get('/api-keys'); setKeys(r.data.data || []); setScopes(r.data.scopes || {}); }
    catch (e) { setError(e.response?.data?.error || 'Failed to load API keys'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function revoke(k) {
    if (!window.confirm(`Revoke "${k.name}"? Pipelines using it will start getting 401 immediately.`)) return;
    try { await axios.delete(`/api-keys/${k.id}`); load(); } catch (e) { alert(e.response?.data?.error || 'Failed to revoke'); }
  }
  async function toggleAudit(k) {
    if (audit[k.id]) { setAudit(a => ({ ...a, [k.id]: null })); return; }
    const r = await axios.get(`/api-keys/${k.id}/audit?limit=50`);
    setAudit(a => ({ ...a, [k.id]: r.data.data || [] }));
  }

  return (
    <div>
      <div className="page-header">
        <h2>API Keys</h2>
        <p>Machine credentials for CI pipelines calling the regression-risk API. Keys are hashed; the plain key is shown once at creation.</p>
      </div>
      <div className="page-body">
        {created && (
          <div className="card" style={{ borderColor: 'var(--success)', marginBottom: 16 }}>
            <strong>Key created: {created.data.name}</strong>
            <p style={{ fontSize: 13 }}>Copy it now and store it as a masked CI variable (<code>RISK_API_KEY</code>). It will not be shown again.</p>
            <pre style={{ userSelect: 'all', background: 'var(--bg-surface)', padding: 10, borderRadius: 6, fontSize: 13 }}>{created.key}</pre>
            <button className="btn btn-secondary" onClick={() => setCreated(null)}>Done</button>
          </div>
        )}
        <div className="toolbar"><div className="spacer" /><button className="btn btn-primary" onClick={() => setShowNew(true)}>New API key</button></div>
        {error && <div className="error-msg">{error}</div>}
        {loading ? <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div> : keys.length === 0
          ? <div className="empty-state"><h3>No API keys</h3><p>Create one for each pipeline or team so it can be revoked on its own.</p></div>
          : (
            <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ textAlign: 'left', fontSize: 12, color: 'var(--text-muted)' }}>
                <th style={{ padding: '8px 10px' }}>Name</th><th style={{ padding: '8px 10px' }}>Prefix</th><th style={{ padding: '8px 10px' }}>Scopes</th>
                <th style={{ padding: '8px 10px' }}>Repos</th><th style={{ padding: '8px 10px' }}>Last used</th><th style={{ padding: '8px 10px' }}>Uses</th>
                <th style={{ padding: '8px 10px' }}>Expires</th><th style={{ padding: '8px 10px' }}>Status</th><th></th></tr></thead>
              <tbody>
                {keys.map(k => (
                  <React.Fragment key={k.id}>
                    <tr style={{ borderTop: '1px solid var(--border)', fontSize: 13, opacity: k.active ? 1 : 0.6 }}>
                      <td style={{ padding: 10 }}>{k.name}<div style={{ fontSize: 11, color: 'var(--text-muted)' }}>by {k.created_by} · {fmt(k.created_at)}</div></td>
                      <td style={{ padding: 10 }}><code>{k.key_prefix}…</code></td>
                      <td style={{ padding: 10 }}>{(k.scopes || []).map(s => <code key={s} style={{ marginRight: 6 }}>{s}</code>)}</td>
                      <td style={{ padding: 10, fontSize: 12 }}>{(k.allowed_repos || []).join(', ') || 'any'}</td>
                      <td style={{ padding: 10, fontSize: 12 }}>{fmt(k.last_used_at)}{k.last_used_ip ? <div style={{ color: 'var(--text-muted)' }}>{k.last_used_ip}</div> : null}</td>
                      <td style={{ padding: 10 }}>{k.use_count}</td>
                      <td style={{ padding: 10, fontSize: 12 }}>{k.expires_at ? fmt(k.expires_at) : 'never'}</td>
                      <td style={{ padding: 10 }}>{k.revoked_at ? <span className="badge badge-cancelled">revoked</span> : k.active ? <span className="badge badge-released">active</span> : <span className="badge badge-cancelled">expired</span>}</td>
                      <td style={{ padding: 10, whiteSpace: 'nowrap' }}>
                        <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => toggleAudit(k)}>{audit[k.id] ? 'Hide audit' : 'Audit'}</button>
                        {!k.revoked_at && <button className="btn btn-ghost" style={{ fontSize: 12, color: 'var(--danger)' }} onClick={() => revoke(k)}>Revoke</button>}
                      </td>
                    </tr>
                    {audit[k.id] && (
                      <tr><td colSpan={9} style={{ padding: '0 10px 10px' }}>
                        {audit[k.id].length === 0 ? <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>No requests yet.</div> : (
                          <table style={{ width: '100%', fontSize: 12 }}><tbody>
                            {audit[k.id].map(a => <tr key={a.id}><td style={{ padding: '2px 6px' }}>{fmt(a.created_at)}</td><td style={{ padding: '2px 6px' }}>{a.action}</td><td style={{ padding: '2px 6px' }}>{a.method} {a.path}</td><td style={{ padding: '2px 6px' }}>{a.status_code}</td><td style={{ padding: '2px 6px' }}>{a.ip}</td><td style={{ padding: '2px 6px', color: 'var(--text-muted)' }}>{a.detail || ''}</td></tr>)}
                          </tbody></table>)}
                      </td></tr>)}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        <RiskSettings />
      </div>
      {showNew && <NewKeyModal scopes={scopes} onClose={() => setShowNew(false)} onCreated={d => { setShowNew(false); setCreated(d); load(); }} />}
    </div>
  );
}
